import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { adminApi } from '../../services/api';
import { 
  FiUsers, 
  FiUserPlus, 
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiUserCheck,
  FiAlertCircle,
  FiTrash2,
  FiAward,
  FiUserMinus 
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function AdminUsersPage() {
  const { user } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [paginationLimit] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState('');

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        limit: paginationLimit,
        search: searchQuery || undefined,
        role: roleFilter === 'all' ? undefined : roleFilter
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) delete params[key];
      });
      
      const response = await adminApi.getUsers(params);
      
      setUsers(response.data.users || []);
      setTotalUsers(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Nepodařilo se načíst uživatele. Zkuste to prosím znovu.');
      toast.error('Chyba při načítání uživatelů');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, roleFilter, paginationLimit]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  // Handle role filter change
  const handleRoleFilterChange = (role) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800';
      case 'USER':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get role label
  const getRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrátor';
      case 'USER':
        return 'Uživatel';
      case 'PENDING':
        return 'Čeká na schválení';
      default:
        return role;
    }
  };

  // Get status color
  const getStatusColor = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  // Get status label
  const getStatusLabel = (isActive) => {
    return isActive ? 'Aktivní' : 'Deaktivován';
  };

  // Approve user
  const handleApprove = async (userId) => {
    try {
      await adminApi.approveUser(userId);
      toast.success('Uživatel schválen');
      fetchUsers();
    } catch (err) {
      console.error('Error approving user:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se schválit uživatele';
      toast.error(message);
    }
  };

  // Make admin
  const handleMakeAdmin = async (userId) => {
    try {
      if (userId === user.id) {
        toast.error('Nelze povýšit sebe sama');
        return;
      }
      
      await adminApi.makeAdmin(userId);
      toast.success('Uživatel povýšen na administrátora');
      fetchUsers();
    } catch (err) {
      console.error('Error making user admin:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se povýšit uživatele';
      toast.error(message);
    }
  };

  // Remove admin
  const handleRemoveAdmin = async (userId) => {
    try {
      if (userId === user.id) {
        toast.error('Nelze odebrat svá vlastní oprávnění');
        return;
      }
      
      await adminApi.removeAdmin(userId);
      toast.success('Administrátorská práva odebrána');
      fetchUsers();
    } catch (err) {
      console.error('Error removing admin:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se odebrat administrátorská práva';
      toast.error(message);
    }
  };

  // Activate user
  const handleActivate = async (userId) => {
    try {
      await adminApi.activateUser(userId);
      toast.success('Uživatel aktivován');
      fetchUsers();
    } catch (err) {
      console.error('Error activating user:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se aktivovat uživatele';
      toast.error(message);
    }
  };

  // Deactivate user
  const handleDeactivate = async (userId) => {
    try {
      if (userId === user.id) {
        toast.error('Nelze deaktivovat sebe sama');
        return;
      }
      
      await adminApi.deactivateUser(userId);
      toast.success('Uživatel deaktivován');
      fetchUsers();
    } catch (err) {
      console.error('Error deactivating user:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se deaktivovat uživatele';
      toast.error(message);
    }
  };

  // Open confirm modal
  const openConfirmModal = (action, userId) => {
    setModalAction(action);
    setSelectedUser(users.find(u => u.id === userId));
    setShowConfirmModal(true);
  };

  // Execute action from modal
  const executeAction = async () => {
    if (!selectedUser || !modalAction) return;
    
    switch (modalAction) {
      case 'approve':
        await handleApprove(selectedUser.id);
        break;
      case 'makeAdmin':
        await handleMakeAdmin(selectedUser.id);
        break;
      case 'removeAdmin':
        await handleRemoveAdmin(selectedUser.id);
        break;
      case 'activate':
        await handleActivate(selectedUser.id);
        break;
      case 'deactivate':
        await handleDeactivate(selectedUser.id);
        break;
      default:
        break;
    }
    
    setShowConfirmModal(false);
    setSelectedUser(null);
    setModalAction('');
  };

  // Get action button
  const getActionButton = (user) => {
    const buttons = [];
    
    if (user.role === 'PENDING') {
      buttons.push(
        <button
          key="approve"
          onClick={() => openConfirmModal('approve', user.id)}
          className="flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
          title="Schválit"
        >
          <FiCheckCircle className="mr-1" />
        </button>
      );
    }
    
    if (user.role !== 'ADMIN' && user.id !== user.id) {
      buttons.push(
        <button
          key="makeAdmin"
          onClick={() => openConfirmModal('makeAdmin', user.id)}
          className="flex items-center px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
          title="Povýšit na admin"
        >
          <FiAward className="mr-1" />
        </button>
      );
    }
    
    if (user.role === 'ADMIN' && user.id !== user.id) {
      buttons.push(
        <button
          key="removeAdmin"
          onClick={() => openConfirmModal('removeAdmin', user.id)}
          className="flex items-center px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
          title="Odebrat admin práva"
        >
          <FiUserMinus className="mr-1" />
        </button>
      );
    }
    
    if (!user.isActive) {
      buttons.push(
        <button
          key="activate"
          onClick={() => openConfirmModal('activate', user.id)}
          className="flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
          title="Aktivovat"
        >
          <FiCheckCircle className="mr-1" />
        </button>
      );
    }
    
    if (user.isActive) {
      buttons.push(
        <button
          key="deactivate"
          onClick={() => openConfirmModal('deactivate', user.id)}
          className="flex items-center px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
          title="Deaktivovat"
        >
          <FiXCircle className="mr-1" />
        </button>
      );
    }
    
    return buttons;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Správa uživatelů</h1>
        <p className="text-sm text-gray-600 mt-1">
          Seznam všech uživatelů systému
        </p>
      </div>

      {/* Loading state */}
      {isLoading && currentPage === 1 ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      ) : (
        <>
          {/* Search and Filter */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hledat
                  </label>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Hledat podle jména, příjmení nebo emailu"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filtr podle role
                  </label>
                  <div className="relative">
                    <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      value={roleFilter}
                      onChange={(e) => handleRoleFilterChange(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="all">Všechny role</option>
                      <option value="ADMIN">Administrátoři</option>
                      <option value="USER">Uživatelé</option>
                      <option value="PENDING">Čekající na schválení</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
                >
                  <FiSearch className="mr-2" />
                  Hledat
                </button>
              </div>
            </form>
          </div>

          {/* Results count */}
          <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
            <div className="flex items-center">
              <FiUsers className="text-blue-600 mr-2" />
              <span className="text-gray-900">
                <strong>{totalUsers}</strong> uživatelů nalezeno
                {searchQuery && ` pro "${searchQuery}"`}
                {roleFilter !== 'all' && ` s rolí ${getRoleLabel(roleFilter)}`}
              </span>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uživatel
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stav
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zaregistrováno
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Akce
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          Rezervací: {user.reservationCount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(user.isActive)}`}>
                          {getStatusLabel(user.isActive)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(user.createdAt), 'dd.MM.yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-1">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowUserDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                            title="Zobrazit detail"
                          >
                            <FiEye />
                          </button>
                          {getActionButton(user)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view */}
            <div className="lg:hidden">
              <div className="divide-y divide-gray-200">
                {users.map(user => (
                  <div key={user.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(user.isActive)}`}>
                            {getStatusLabel(user.isActive)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Zaregistrováno: {format(new Date(user.createdAt), 'dd.MM.yyyy')}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Zobrazit"
                        >
                          <FiEye />
                        </button>
                        {getActionButton(user)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-gray-600">
                  Zobrazuji {((currentPage - 1) * paginationLimit) + 1} - {Math.min(currentPage * paginationLimit, totalUsers)} z {totalUsers}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <FiChevronLeft className="text-gray-600" />
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page = currentPage - 2 + i;
                    if (page < 1) page = 1;
                    if (page > totalPages) page = totalPages;
                    
                    // Skip duplicates
                    if (i > 0 && page === currentPage - 2 + (i - 1)) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          currentPage === page 
                            ? 'bg-blue-600 text-white' 
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <FiChevronRight className="text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Detail Modal */}
      {showUserDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Detail uživatele</h2>
              <button
                onClick={() => setShowUserDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-white">
                    {selectedUser.firstName.charAt(0)}{selectedUser.lastName.charAt(0)}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedUser.firstName} {selectedUser.lastName}
                </h3>
                <p className="text-gray-500">{selectedUser.email}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Role</div>
                  <span className={`inline-block px-2 py-1 rounded font-medium ${getRoleColor(selectedUser.role)}`}>
                    {getRoleLabel(selectedUser.role)}
                  </span>
                </div>
                <div>
                  <div className="text-gray-500">Stav</div>
                  <span className={`inline-block px-2 py-1 rounded font-medium ${getStatusColor(selectedUser.isActive)}`}>
                    {getStatusLabel(selectedUser.isActive)}
                  </span>
                </div>
                <div>
                  <div className="text-gray-500">Rezervací</div>
                  <div className="font-medium text-gray-900">{selectedUser.reservationCount || 0}</div>
                </div>
                <div>
                  <div className="text-gray-500">Zaregistrováno</div>
                  <div className="font-medium text-gray-900">
                    {format(new Date(selectedUser.createdAt), 'dd.MM.yyyy HH:mm')}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowUserDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                >
                  Zavřít
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {modalAction === 'deactivate' || modalAction === 'removeAdmin' ? (
                    <FiAlertCircle className="w-6 h-6 text-red-500" />
                  ) : (
                    <FiUserCheck className="w-6 h-6 text-blue-500" />
                  )}
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {modalAction === 'approve' && 'Schválit uživatele'}
                    {modalAction === 'makeAdmin' && 'Povýšit na administrátora'}
                    {modalAction === 'removeAdmin' && 'Odebrat administrátorská práva'}
                    {modalAction === 'activate' && 'Aktivovat uživatele'}
                    {modalAction === 'deactivate' && 'Deaktivovat uživatele'}
                  </h3>
                  <div className="mt-2 text-sm text-gray-500">
                    <p>
                      {modalAction === 'approve' && `Opravdu chcete schválit uživatele ${selectedUser.firstName} ${selectedUser.lastName}?`}
                      {modalAction === 'makeAdmin' && `Opravdu chcete povýšit ${selectedUser.firstName} ${selectedUser.lastName} na administrátora?`}
                      {modalAction === 'removeAdmin' && `Opravdu chcete odebrat administrátorská práva uživateli ${selectedUser.firstName} ${selectedUser.lastName}?`}
                      {modalAction === 'activate' && `Opravdu chcete aktivovat uživatele ${selectedUser.firstName} ${selectedUser.lastName}?`}
                      {modalAction === 'deactivate' && `Opravdu chcete deaktivovat uživatele ${selectedUser.firstName} ${selectedUser.lastName}? Všechny budoucí rezervace budou zrušeny.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-2 rounded-b-xl">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedUser(null);
                  setModalAction('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
              >
                Zrušit
              </button>
              <button
                onClick={executeAction}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
              >
                <FiCheckCircle className="mr-2" />
                Potvrdit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsersPage;
